import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../services/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMessageCircle, FiMail, FiLock, FiArrowRight, FiCheckCircle, FiShield, FiLoader, FiAlertCircle, FiRefreshCw, FiUser } = FiIcons;

const RESEND_COOLDOWN_SECONDS = 30;

const COUNTRY_CODES = [
  { code: '+91', country: 'IN' },
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'AU' },
  { code: '+65', country: 'SG' },
];

function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState(location.state?.mode === 'register' ? 'register' : 'login'); // 'login' | 'register'
  const [step, setStep] = useState(1); // 1: Contact, 2: OTP, 3: Success Transition

  // Login: one field, either email or WhatsApp number.
  const [loginContact, setLoginContact] = useState('');

  // Register: all three, always.
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');

  // What OTP step 2/resend actually targets — set once the request succeeds.
  const [otpTarget, setOtpTarget] = useState({ identifier: '', display: '' });

  const [otpInputs, setOtpInputs] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [transitionMessage, setTransitionMessage] = useState('Authenticating...');
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const from = location.state?.from?.pathname || '/details';

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const requestOtpFor = async (identifier, display) => {
    try {
      await api.requestOtp({ identifier });
      setOtpTarget({ identifier, display });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.payload?.notRegistered) {
        setError('You are not registered. Please sign up to continue.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again later.');
      }
      return false;
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const value = loginContact.trim();
    if (!value) {
      setError('Please enter your email or WhatsApp number.');
      return;
    }

    setIsLoading(true);
    const sent = await requestOtpFor(value, value);
    setIsLoading(false);
    if (sent) advanceToOtpStep();
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (name.trim().length < 2) {
      setError('Please enter your name.');
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (phoneNumber.length < 7) {
      setError('Please enter a valid WhatsApp number.');
      return;
    }

    const whatsapp = `${countryCode}${phoneNumber}`;

    setIsLoading(true);
    try {
      await api.register({ name: name.trim(), email, whatsapp });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 409)) {
        setError(err.message || 'Failed to register. Please try again.');
        setIsLoading(false);
        return;
      }
      // Already registered — treat as a normal login for this contact.
    }
    // OTP goes to WhatsApp by default — fastest to receive and confirm.
    const sent = await requestOtpFor(whatsapp, `${countryCode} ${phoneNumber}`);
    setIsLoading(false);
    if (sent) advanceToOtpStep();
  };

  const advanceToOtpStep = () => {
    setStep(2);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtpInputs(['', '', '', '', '', '']);
    setTimeout(() => otpRefs[0].current?.focus(), 100);
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isLoading || !otpTarget.identifier) return;
    setError('');
    setIsLoading(true);
    const sent = await requestOtpFor(otpTarget.identifier, otpTarget.display);
    setIsLoading(false);
    if (sent) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setOtpInputs(['', '', '', '', '', '']);
      otpRefs[0].current?.focus();
    }
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtpInputs = [...otpInputs];
    newOtpInputs[index] = value;
    setOtpInputs(newOtpInputs);
    setError('');

    if (value !== '' && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpInputs[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const enteredOtp = otpInputs.join('');

    if (enteredOtp.length < 6) {
      setError('Please enter the complete 6-digit OTP.');
      return;
    }

    setIsLoading(true);
    let result;
    try {
      result = await api.verifyOtp({ identifier: otpTarget.identifier, otp: enteredOtp });
    } catch (err) {
      setError(err.message || 'Incorrect OTP. Please try again.');
      setOtpInputs(['', '', '', '', '', '']);
      otpRefs[0].current?.focus();
      setIsLoading(false);
      return;
    }
    setIsLoading(false);

    // Success! Start redirection UX
    setStep(3);

    setTimeout(() => setTransitionMessage('Verifying subscriber status...'), 800);
    setTimeout(() => setTransitionMessage('Secure connection established.'), 1800);
    setTimeout(() => setTransitionMessage('Welcome to iNVESTT!'), 2600);

    setTimeout(() => {
      login(result.token, result.user);
      const destination = result.user.subscriptionStatus === 'active' ? from : '/pending-approval';
      navigate(destination, { replace: true });
    }, 3500);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750919065872-investt%20png.png"
            alt="iNVESTT.in"
            className="h-12 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Secure Access</h2>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative">
          <AnimatePresence mode="wait">

            {/* STEP 1: LOGIN or REGISTER */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-6 sm:p-8"
              >
                <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'login' ? 'bg-white shadow-sm text-[#19b48d]' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Log In
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('register'); setError(''); }}
                    className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${mode === 'register' ? 'bg-white shadow-sm text-[#19b48d]' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Sign Up
                  </button>
                </div>

                {mode === 'login' ? (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiShield} className="text-[#19b48d]" />
                        <h3 className="font-bold text-gray-900">Subscriber Login</h3>
                      </div>
                      <p className="text-sm text-gray-500">
                        Login is exclusively available for registered iNVESTT subscribers.
                      </p>
                    </div>

                    <form onSubmit={handleLoginSubmit} className="space-y-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                          Email or WhatsApp Number
                        </label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={loginContact}
                            onChange={(e) => setLoginContact(e.target.value)}
                            placeholder="you@example.com or 98765 43210"
                            className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-bold text-gray-900 transition-all"
                            required
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">We'll send a one-time code to whichever you use here.</p>
                      </div>

                      {error && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col space-y-3 text-red-500 text-sm font-medium bg-red-50 p-4 rounded-xl border border-red-100">
                          <div className="flex items-start space-x-2">
                            <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                          </div>
                          {error.includes('not registered') && (
                            <button
                              type="button"
                              onClick={() => { setMode('register'); setError(''); }}
                              className="w-full py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-colors shadow-sm"
                            >
                              Sign Up Now
                            </button>
                          )}
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full h-12 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all flex items-center justify-center space-x-2 disabled:opacity-70 mt-2"
                      >
                        {isLoading ? (
                          <SafeIcon icon={FiLoader} className="animate-spin" />
                        ) : (
                          <>
                            <span>Send OTP</span>
                            <SafeIcon icon={FiArrowRight} />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiShield} className="text-[#19b48d]" />
                        <h3 className="font-bold text-gray-900">Create Your Account</h3>
                      </div>
                      <p className="text-sm text-gray-500">
                        Sign up with your name, email, and WhatsApp number — activation is confirmed shortly after.
                      </p>
                    </div>

                    <form onSubmit={handleRegisterSubmit} className="space-y-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Your Name</label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name"
                            className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-bold text-gray-900 transition-all"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Email Address</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your.name@example.com"
                            className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-bold text-gray-900 transition-all"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">WhatsApp Number</label>
                        <div className="flex space-x-2">
                          <div className="w-1/3">
                            <select
                              value={countryCode}
                              onChange={(e) => setCountryCode(e.target.value)}
                              className="w-full h-12 px-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-bold text-gray-700 appearance-none"
                            >
                              {COUNTRY_CODES.map(c => (
                                <option key={c.code} value={c.code}>{c.country} ({c.code})</option>
                              ))}
                            </select>
                          </div>
                          <div className="w-2/3 relative">
                            <SafeIcon icon={FiMessageCircle} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="tel"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                              placeholder="Your number"
                              className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-bold text-gray-900 transition-all"
                              required
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">Your OTP will be sent here.</p>
                      </div>

                      {error && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center space-x-2 text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg">
                          <SafeIcon icon={FiAlertCircle} />
                          <span>{error}</span>
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full h-12 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all flex items-center justify-center space-x-2 disabled:opacity-70 mt-2"
                      >
                        {isLoading ? (
                          <SafeIcon icon={FiLoader} className="animate-spin" />
                        ) : (
                          <>
                            <span>Sign Up & Send OTP</span>
                            <SafeIcon icon={FiArrowRight} />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}

                <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-600">Want to see what's included first?</p>
                  <Link
                    to="/pricing"
                    className="inline-block mt-2 font-bold text-[#19b48d] hover:text-[#16a085] transition-colors"
                  >
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            )}

            {/* STEP 2: OTP VERIFICATION */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-6 sm:p-8"
              >
                <div className="mb-6">
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm font-bold text-gray-400 hover:text-gray-900 mb-4 transition-colors"
                  >
                    ← Back
                  </button>
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiLock} className="text-[#19b48d]" />
                    <h3 className="font-bold text-gray-900">Enter OTP</h3>
                  </div>
                  <p className="text-sm text-gray-500">
                    We've sent a 6-digit code to <span className="font-bold text-gray-900">{otpTarget.display}</span>.
                  </p>
                </div>

                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  <div className="flex justify-between gap-1.5 sm:gap-2">
                    {otpInputs.map((digit, index) => (
                      <input
                        key={index}
                        ref={otpRefs[index]}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-black bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none transition-all"
                      />
                    ))}
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center space-x-2 text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg">
                      <SafeIcon icon={FiAlertCircle} />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all flex items-center justify-center space-x-2 disabled:opacity-70"
                  >
                    {isLoading ? (
                      <SafeIcon icon={FiLoader} className="animate-spin" />
                    ) : (
                      <>
                        <span>Verify & Login</span>
                        <SafeIcon icon={FiCheckCircle} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || isLoading}
                    className="w-full flex items-center justify-center space-x-2 text-sm font-bold text-gray-500 hover:text-[#19b48d] transition-colors disabled:opacity-50 disabled:hover:text-gray-500"
                  >
                    <SafeIcon icon={FiRefreshCw} className="text-xs" />
                    <span>{resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}</span>
                  </button>
                </form>
              </motion.div>
            )}

            {/* STEP 3: REDIRECTION UX */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-12 flex flex-col items-center justify-center text-center space-y-6"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut",
                    times: [0, 0.5, 1],
                    repeat: Infinity
                  }}
                  className="w-16 h-16 rounded-2xl bg-[#19b48d]/10 flex items-center justify-center"
                >
                  <SafeIcon icon={FiShield} className="text-[#19b48d] text-3xl" />
                </motion.div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black text-gray-900">Access Granted</h3>
                  <motion.p
                    key={transitionMessage}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm font-medium text-[#19b48d]"
                  >
                    {transitionMessage}
                  </motion.p>
                </div>

                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <motion.div
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 3.5, ease: "linear" }}
                    className="h-full bg-[#19b48d]"
                  />
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;